import { css, html, LitElement } from "lit";
import { property } from "lit/decorators/property.js";
import { queryAll } from "lit/decorators/query-all.js";
import debounce from "lodash-es/debounce";
import "../../shared/components/animated-counter";
import { cssReset } from "../../shared/css-reset";
import { defineCustomElement } from "../../shared/defineCustomElement";
import { _cssVariableMap } from "../../shared/fragments/css-variable-map";
import { DEFAULT_BASE_URL, SUPPORTED_LANGUAGES } from "../../api-sdk/constants";
import { TNumericId } from "../../shared/types";
import { LANGUAGES } from "../../api-sdk/types";
import { getCumulativeImpact } from "../../api-sdk/v3/routes";
import { enforceConfig } from "../../shared/enforce-config";
import { BeamError, MissingConfig } from "../../utils/beam-errors";
import { AsyncController } from "../../shared/controllers/async-controller";
import { _errorMessage } from "../../shared/fragments/error-message-template";
import { _loading } from "../../shared/fragments/loading-template";
import { syncElementWidths } from "../../shared/sync-element-widths";
import { localizeUserString } from "../../shared/localize";
import { defineCustomText, useCustomText } from "../../shared/fragments/custom-text";
import { strings } from "./strings";

type RequiredConfig = {
  apiKey: string;
  chainId: TNumericId;
};

type IAPIV3CumulativeImpact = Awaited<ReturnType<typeof getCumulativeImpact>>;

const MAX_COUNTER_ANIMATION_DURATION = 1.5 * 1000;

export class BeamCumulativeImpact extends LitElement {
  static tagName = "beam-cumulative-impact";

  @property({ type: String }) public apiKey?: RequiredConfig["apiKey"];

  @property({ type: Number }) public chainId?: RequiredConfig["chainId"];

  @property({ type: String }) public baseUrl = DEFAULT_BASE_URL;

  @property({ type: String }) public lang: LANGUAGES = "en";

  @property({ type: Boolean }) public disableAnimation = false;

  @property({ type: Boolean }) public debug = false;

  @property({ type: Boolean }) public draftConfig = false;

  get configLang() {
    return SUPPORTED_LANGUAGES[this.lang] || "en";
  }

  private getCumulativeImpactData = async () => {
    if (!enforceConfig<RequiredConfig>(["apiKey", "chainId"], this)) {
      throw new MissingConfig();
    }
    const res = await getCumulativeImpact({
      baseUrl: this.baseUrl,
      pathParams: {
        chainId: this.chainId,
      },
      headers: {
        authorization: `Api-Key ${this.apiKey}`,
      },
      queryParams: {
        version: "1.0.0",
        lang: this.configLang,
        draftConfig: this.draftConfig,
      },
    });

    return res;
  };

  private cumulativeImpactDataController = new AsyncController<typeof this.getCumulativeImpactData>(
    this,
    this.getCumulativeImpactData
  );

  @queryAll(".impact-row-value-container")
  impactRowRefs!: HTMLElement[];
  resizeElements = debounce(
    () => {
      syncElementWidths(this.impactRowRefs);
    },
    50,
    { maxWait: 50, leading: true }
  );

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("resize", this.resizeElements);
  }

  async updated(changedProperties: Map<string, unknown>) {
    // Reload nonprofit list on change of any of these props:
    // Also fires on first load as props go from undefined => value
    const requireNewDataProps = ["chainId", "baseUrl", "lang"];
    for (const prop of requireNewDataProps) {
      if (changedProperties.has(prop)) {
        await this.cumulativeImpactDataController.exec();
        break;
      }
    }

    this.resizeElements();
  }

  render() {
    const { data, error, loading } = this.cumulativeImpactDataController;
    if (loading) {
      return _loading();
    }
    if (error) {
      if (this.debug) {
        return _errorMessage({ error });
      }
      return "";
    }
    if (data == null) {
      if (this.debug) {
        return _errorMessage({ error: new BeamError("Missing data") });
      }
      return "";
    }

    return html`
      <style>
        :host {
          ${this.cssVariables.toCSS()}
        }
      </style>
      <div style="display: flex; flex-direction: column;" class="root">
        <div
          style="margin: 10px 0 0 0;"
          tabindex="-1"
          class="options"
          part="options"
          aria-labelledby="beam-RedeemTransaction-title"
        >
          ${this.renderCumulativeImpact(data)}
        </div>
      </div>
    `;
  }

  private renderCumulativeImpact(data: IAPIV3CumulativeImpact) {
    const rawValues = data.impacts ? data.impacts?.map((impact) => impact.value || 0) : [];
    const maxRawValue = Math.max(...rawValues);
    const durations = rawValues.map(
      (val) => (MAX_COUNTER_ANIMATION_DURATION * Math.log(Math.max(val, 1))) / Math.log(maxRawValue)
    );
    return html`
      <div class="main">
        <div class="title" role="heading">
          ${localizeUserString(this.configLang, data?.config?.web?.title || "") ||
          strings[this.configLang].cumulativeImpactTitle()}
        </div>
        <p class="description">
          ${localizeUserString(this.configLang, data?.config?.web?.description || "") ||
          strings[this.configLang].cumulativeImpactDescription()}
        </p>
        <div class="impact-list">
          ${data.impacts?.map((impact: any, idx: number) => {
            return this.renderCumulativeImpactRow({ impact, animationDuration: durations[idx] });
          })}
        </div>
      </div>
    `;
  }

  private renderCumulativeImpactRow({ impact, animationDuration }: { impact: any; animationDuration: number }) {
    const disableAnimation = this.cumulativeImpactDataController.data?.config?.web.disableAnimation;
    const floatingCounterClass = "impact-row-value-counter impact-row-value-floating-counter";
    const duration = disableAnimation ? 0 : animationDuration;

    const unitDisplay = localizeUserString(this.configLang, impact.unit || "");
    const summaryDisplay = localizeUserString(this.configLang, impact.summary || "");

    /**
     * because we set the width using JavaScript by forcing all impact-row-value-container elements
     * to match the width of the widest sibling, we use a hidden span with the total preloaded to
     * ensure that impact-row-value-container widths do not change as the animation runs.
     */
    return html`
      <div class="impact-row">
        <div class="impact-row-value-container">
          <span class="impact-row-value-counter" style="visibility: hidden;" aria-hidden="true">
            ${impact.value.toLocaleString()}
          </span>
            <animated-counter
              class="${floatingCounterClass}"
              finalValue=${impact.value}
              duration=${duration}
            ></animated-counter>
          </span>
        </div>
        <div class="impact-row-description">${unitDisplay} ${summaryDisplay}</div>
      </div>
    `;
  }

  public get cssVariables() {
    const defaults: Record<string, string> = {
      "--beam-fontFamily": "inherit",
      "--beam-fontStyle": "inherit",
      "--beam-fontSize": "16px",
      "--beam-lineHeight": "1",
      "--beam-textColor": "inherit",
      // main
      ...defineCustomText("--beam-CumulativeImpact-title", {
        fontSize: "32px",
      }),
      ...defineCustomText("--beam-CumulativeImpact-description", {
        marginTop: "16px",
        lineHeight: "1.3",
      }),
      "--beam-CumulativeImpact-impact-list-marginTop": "24px",
      "--beam-CumulativeImpact-row-marginTop": "24px",
      // row value container
      "--beam-CumulativeImpact-row-value-container-borderBottomWidth": "4px",
      "--beam-CumulativeImpact-row-value-container-borderBottomColor": "currentColor",
      "--beam-CumulativeImpact-row-value-container-paddingTop": "0",
      "--beam-CumulativeImpact-row-value-container-paddingRight": "20px",
      "--beam-CumulativeImpact-row-value-container-paddingBottom": "4px",
      "--beam-CumulativeImpact-row-value-container-paddingLeft": "20px",
      // row value counter
      ...defineCustomText("--beam-CumulativeImpact-row-value-counter", {
        color: "currentColor",
        fontSize: "24px",
        fontWeight: "normal",
      }),
      // row description
      ...defineCustomText("--beam-CumulativeImpact-row-description", {
        color: "currentColor",
        fontSize: "16px",
        fontWeight: "normal",
        lineHeight: "1.3",
      }),
      "--beam-CumulativeImpact-row-description-paddingLeft": "24px",
      "--beam-CumulativeImpact-row-description-paddingRight": "24px",
      "--beam-CumulativeImpact-row-description-mobile-marginTop": "12px",
    };

    const remoteConfig = this.cumulativeImpactDataController.data?.config?.web?.theme || {};

    const config = { ...defaults, ...remoteConfig };

    const serializable = Object.create({
      toCSS() {
        return _cssVariableMap(this as Record<string, string>);
      },
    });

    return Object.assign(serializable, config);
  }

  static styles = [
    cssReset,
    css`
      :host {
        display: block;
        font-family: var(--beam-fontFamily, inherit);
        font-style: var(--beam-fontStyle, inherit);
        font-size: var(--beam-fontSize, inherit);
        color: var(--beam-textColor, inherit);
        background-color: var(--beam-backgroundColor, inherit);
        line-height: var(--beam-lineHeight, 1);
        word-break: normal;
      }
      .main {
        text-align: left;
      }
      .title {
        ${useCustomText("--beam-CumulativeImpact-title")}
        text-align: inherit;
      }
      .description {
        ${useCustomText("--beam-CumulativeImpact-description")}
        text-align: inherit;
      }
      .impact-list {
        display: flex;
        flex-direction: column;
        margin-top: var(--beam-CumulativeImpact-impact-list-marginTop);
      }
      .impact-row {
        display: flex;
        align-items: center;
        margin-top: var(--beam-CumulativeImpact-row-marginTop);
      }
      .impact-row-value-container {
        position: relative;
        border-bottom-width: var(--beam-CumulativeImpact-row-value-container-borderBottomWidth);
        border-bottom-color: var(--beam-CumulativeImpact-row-value-container-borderBottomColor);
        border-bottom-style: solid;
        padding-top: var(--beam-CumulativeImpact-row-value-container-paddingTop);
        padding-right: var(--beam-CumulativeImpact-row-value-container-paddingRight);
        padding-bottom: var(--beam-CumulativeImpact-row-value-container-paddingBottom);
        padding-left: var(--beam-CumulativeImpact-row-value-container-paddingLeft);
        text-align: center;
        flex-shrink: 0;
      }
      .impact-row-value-counter {
        ${useCustomText("--beam-CumulativeImpact-row-value-counter")}
      }
      .impact-row-value-floating-counter {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
      }
      .impact-row-description {
        ${useCustomText("--beam-CumulativeImpact-row-description")}
        padding-left: var(--beam-CumulativeImpact-row-description-paddingLeft);
        padding-right: var(--beam-CumulativeImpact-row-description-paddingRight);
        /* match padding of value of counter for proper centering */
        padding-bottom: var(--beam-CumulativeImpact-row-value-paddingBottom);
        text-align: inherit;
        flex-grow: 1;
      }
      @media (max-width: 500px) {
        .main {
          text-align: center;
        }
        .impact-row {
          flex-direction: column;
        }
        .impact-row-description {
          margin-top: var(--beam-CumulativeImpact-row-description-mobile-marginTop);
          padding-bottom: 0;
        }
      }
    `,
  ];
}

defineCustomElement(BeamCumulativeImpact);

declare global {
  interface HTMLElementTagNameMap {
    "beam-cumulative-impact": BeamCumulativeImpact;
  }
}
